import { createApi } from '@reduxjs/toolkit/query/react';
import { deviceInfoAxiosQuery } from '../../utils/axios/deviceInfo.axios';
import {
    ICreatePromptPayload,
    IPrompt,
    IPromptConfiguration,
    IPromptHistoryEntry,
    IPromptHistoryResult,
    IPromptsListResponse,
    IPromptsListResult,
    IPromptsPage,
    IUpdatePromptPayload,
    getPromptId,
} from '../../features/dashboard/prompts/types';

const defaultPage = (total = 0): IPromptsPage => ({
    size: 10,
    totalElements: total,
    totalPages: 1,
    number: 1,
    hasNextPage: false,
});

const unwrapPrompt = (response: unknown): IPrompt => {
    const wrapped = response as { data?: IPrompt };
    return (wrapped?.data ?? response) as IPrompt;
};

const normalizeList = (response: unknown): IPromptsListResult => {
    const apiResponse = response as IPromptsListResponse;
    const elements = apiResponse?.data?.elements ?? [];
    const page = apiResponse?.data?.page ?? defaultPage(elements.length);
    return { elements, page };
};

const normalizeHistoryEntry = (response: unknown): IPromptHistoryEntry => {
    const wrapped = response as {
        data?: IPromptHistoryEntry & {
            historyEntry?: IPromptHistoryEntry;
            changes?: Record<string, unknown>;
        };
        historyEntry?: IPromptHistoryEntry;
        changes?: Record<string, unknown>;
    };
    const data = wrapped?.data;
    if (data?.historyEntry) {
        return {
            ...data.historyEntry,
            _id: data._id || data.historyEntry._id,
            name: data.name || data.historyEntry.name,
            key: data.key || data.historyEntry.key,
            changes: data.changes,
        };
    }
    if (wrapped?.historyEntry) {
        return {
            ...wrapped.historyEntry,
            changes: wrapped.changes ?? wrapped.historyEntry.changes,
        };
    }
    if (data) return data;
    return response as IPromptHistoryEntry;
};

const flattenHistoryItem = (item: unknown): IPromptHistoryEntry => {
    const entry = item as IPromptHistoryEntry & { historyEntry?: IPromptHistoryEntry };
    if (entry?.historyEntry) {
        return {
            ...entry.historyEntry,
            _id: entry._id || entry.historyEntry._id,
            name: entry.historyEntry.name || entry.name,
            key: entry.historyEntry.key || entry.key,
        };
    }
    return entry;
};

const normalizeHistory = (response: unknown): IPromptHistoryResult => {
    if (Array.isArray(response)) {
        const elements = (response as unknown[]).map(flattenHistoryItem);
        return { elements, page: defaultPage(elements.length) };
    }

    const wrapped = response as {
        data?: {
            elements?: unknown[];
            history?: unknown[];
            page?: IPromptsPage;
        };
        elements?: unknown[];
        history?: unknown[];
        page?: IPromptsPage;
    };

    const data = wrapped.data;
    const rawElements =
        (data && Array.isArray(data.history) && data.history) ||
        (data && Array.isArray(data.elements) && data.elements) ||
        (Array.isArray(wrapped.history) && wrapped.history) ||
        (Array.isArray(wrapped.elements) && wrapped.elements) ||
        [];

    const elements = rawElements.map(flattenHistoryItem);
    const page = data?.page ?? wrapped.page ?? defaultPage(elements.length);
    return { elements, page };
};

export const promptsApi = createApi({
    reducerPath: 'promptsApi',
    baseQuery: deviceInfoAxiosQuery(),
    tagTypes: ['Prompts', 'Prompt', 'PromptHistory'],
    endpoints: (builder) => ({
        getPromptsList: builder.query<
            IPromptsListResult,
            { page?: number; size?: number; archived?: boolean } | void
        >({
            query: (params) => {
                const page = params?.page ?? 1;
                const size = params?.size ?? 10;
                const archived = params?.archived ?? false;
                return {
                    url: `/prompts?page=${page}&size=${size}&archived=${archived}`,
                    method: 'GET',
                };
            },
            transformResponse: normalizeList,
            providesTags: (result) =>
                result
                    ? [
                        ...result.elements.map((item) => ({
                            type: 'Prompt' as const,
                            id: getPromptId(item),
                        })),
                        { type: 'Prompts', id: 'LIST' },
                    ]
                    : [{ type: 'Prompts', id: 'LIST' }],
        }),
        getPromptById: builder.query<IPrompt, string>({
            query: (id) => ({
                url: `/prompts/${id}`,
                method: 'GET',
            }),
            transformResponse: unwrapPrompt,
            providesTags: (_, __, id) => [{ type: 'Prompt', id }],
        }),
        getPromptHistory: builder.query<
            IPromptHistoryResult,
            { id: string; page?: number; size?: number }
        >({
            query: ({ id, page = 1, size = 10 }) => ({
                url: `/prompts/${id}/history?page=${page}&size=${size}`,
                method: 'GET',
            }),
            transformResponse: normalizeHistory,
            providesTags: (_, __, arg) => [{ type: 'PromptHistory', id: arg.id }],
        }),
        getPromptHistoryVersion: builder.query<
            IPromptHistoryEntry,
            { id: string; version: number }
        >({
            query: ({ id, version }) => ({
                url: `/prompts/${id}/history/${version}`,
                method: 'GET',
            }),
            transformResponse: normalizeHistoryEntry,
        }),
        createPrompt: builder.mutation<IPrompt, ICreatePromptPayload>({
            query: (data) => ({
                url: '/prompts',
                method: 'POST',
                data,
            }),
            transformResponse: unwrapPrompt,
            invalidatesTags: [{ type: 'Prompts', id: 'LIST' }],
        }),
        updatePrompt: builder.mutation<IPrompt, { id: string; body: IUpdatePromptPayload }>({
            query: ({ id, body }) => ({
                url: `/prompts/${id}`,
                method: 'PUT',
                data: body,
            }),
            transformResponse: unwrapPrompt,
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        promptsApi.util.updateQueryData('getPromptById', id, () => data),
                    );
                } catch {
                    // leave cache unchanged on failure
                }
            },
            invalidatesTags: (_, __, arg) => [
                { type: 'Prompt', id: arg.id },
                { type: 'PromptHistory', id: arg.id },
                { type: 'Prompts', id: 'LIST' },
            ],
        }),
        restorePromptVersion: builder.mutation<IPrompt, { id: string; version: number }>({
            query: ({ id, version }) => ({
                url: `/prompts/${id}/restore/${version}`,
                method: 'POST',
            }),
            transformResponse: unwrapPrompt,
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        promptsApi.util.updateQueryData('getPromptById', id, () => data),
                    );
                } catch {
                    // leave cache unchanged on failure
                }
            },
            invalidatesTags: (_, __, arg) => [
                { type: 'Prompt', id: arg.id },
                { type: 'PromptHistory', id: arg.id },
                { type: 'Prompts', id: 'LIST' },
            ],
        }),
        publishPrompt: builder.mutation<IPrompt, string>({
            query: (id) => ({
                url: `/prompts/${id}/publish`,
                method: 'POST',
            }),
            transformResponse: unwrapPrompt,
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        promptsApi.util.updateQueryData('getPromptById', id, () => data),
                    );
                } catch {
                    // leave cache unchanged on failure
                }
            },
            invalidatesTags: (_, __, id) => [
                { type: 'Prompt', id },
                { type: 'PromptHistory', id },
                { type: 'Prompts', id: 'LIST' },
            ],
        }),
        archivePrompt: builder.mutation<IPrompt, string>({
            query: (id) => ({
                url: `/prompts/${id}/archive`,
                method: 'POST',
            }),
            transformResponse: unwrapPrompt,
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        promptsApi.util.updateQueryData('getPromptById', id, () => data),
                    );
                } catch {
                    // leave cache unchanged on failure
                }
            },
            invalidatesTags: (_, __, id) => [
                { type: 'Prompt', id },
                { type: 'PromptHistory', id },
                { type: 'Prompts', id: 'LIST' },
            ],
        }),
        unarchivePrompt: builder.mutation<IPrompt, string>({
            query: (id) => ({
                url: `/prompts/${id}/unarchive`,
                method: 'POST',
            }),
            transformResponse: unwrapPrompt,
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        promptsApi.util.updateQueryData('getPromptById', id, () => data),
                    );
                } catch {
                    // leave cache unchanged on failure
                }
            },
            invalidatesTags: (_, __, id) => [
                { type: 'Prompt', id },
                { type: 'PromptHistory', id },
                { type: 'Prompts', id: 'LIST' },
            ],
        }),
    }),
});

export const {
    useGetPromptsListQuery,
    useGetPromptByIdQuery,
    useGetPromptHistoryQuery,
    useGetPromptHistoryVersionQuery,
    useLazyGetPromptHistoryVersionQuery,
    useCreatePromptMutation,
    useUpdatePromptMutation,
    useRestorePromptVersionMutation,
    usePublishPromptMutation,
    useArchivePromptMutation,
    useUnarchivePromptMutation,
} = promptsApi;

export type { ICreatePromptPayload, IPromptConfiguration, IUpdatePromptPayload };
